'use client'
import { usePathname } from 'next/navigation'
import { useCallback, useEffect } from 'react'

import postNotificationSettings from '@/libs/actions/notifications/post-notification-settings'
import useGetNotificationSettingsQuery from '@/libs/apis/queries/use-get-notification-settings'
import { AMP_EVENT_NAME, trackAmpEvent } from '@/libs/configs/amplitude'
import postponeNotificationMaxHour from '@/libs/constants/postpone-notification-max-hour'
import getErrorMessage from '@/libs/helpers/get-error-message'
import { useAppDispatch } from '@/libs/hooks/use-app-dispatch'
import { useAppSelector } from '@/libs/hooks/use-app-selector'
import useFcmToken from '@/libs/hooks/use-fcm-token'
import useToast from '@/libs/hooks/use-toast'
import useToastDialog from '@/libs/hooks/use-toast-dialog'
import {
  setPostponeNotificationDate,
  setShowNotificationRequest,
} from '@/libs/store/slice/ui-slice'

export default function NotificationRequest() {
  const pathname = usePathname()

  const { show: showToast, close: closeToast } = useToast()
  const { show: showToastDialog, close: closeToastDialog } = useToastDialog()
  const { notificationPermissionStatus, requestNotificationPermission } = useFcmToken()
  const dispatch = useAppDispatch()

  const { data: notificationSettings } = useGetNotificationSettingsQuery()

  const { postponeNotificationDate, showNotificationRequest } = useAppSelector((state) => state.ui)
  const { partnerId, partnerIdNonIntegration } = useAppSelector((state) => state.user)

  const hasPartnerId = partnerId || partnerIdNonIntegration

  const handlePostNotificationSettings = async () => {
    const result = await postNotificationSettings({
      type: 'PUSH_NOTIF',
      value: true,
    })

    dispatch(setShowNotificationRequest(false))

    if (!result.success) {
      return showToast({
        title: result?.error?.code ?? 'Error Occured',
        description: result?.error?.message,
        type: 'error',
        traceId: result?.traceId,
      })
    }

    return showToast({
      type: 'success',
      title: 'We’ll notify you of new surveys.',
      onClose: closeToast,
    })
  }

  const grantNotification = async () => {
    trackAmpEvent(AMP_EVENT_NAME.CLICK_PUSH_NOTIFICATION_PROMPT, {
      'Notification Prompt Button': 'Allow',
    })

    await postNotificationSettings({ value: true, type: 'PUSH_NOTIF' }).then(async () => {
      dispatch(setShowNotificationRequest(false))
      closeToastDialog()
      return await requestNotificationPermission()
        .then(async () => {
          return handlePostNotificationSettings()
        })
        .catch((error) => {
          showToast({
            type: 'warning',
            title: getErrorMessage(error),
            onClose: closeToast,
          })
        })
    })
  }

  const blockNotification = (ctaType: 'Later' | 'Close') => {
    trackAmpEvent(AMP_EVENT_NAME.CLICK_PUSH_NOTIFICATION_PROMPT, {
      'Notification Prompt Button': ctaType,
    })

    const currentDate = new Date()
    const postponeNotificationDate = new Date(
      currentDate.getTime() + postponeNotificationMaxHour * 60 * 60 * 1000,
    )

    dispatch(setShowNotificationRequest(false))
    dispatch(setPostponeNotificationDate(postponeNotificationDate.toString()))
    return closeToastDialog()
  }

  const handleNotificationRequest = useCallback(
    async (status: boolean) => {
      if (pathname === '/suspend') {
        return dispatch(setShowNotificationRequest(false))
      }

      if (!status) {
        return
      }

      const itHasNotification = 'Notification' in window
      const isPostponeDateExpired =
        !postponeNotificationDate || new Date(postponeNotificationDate) <= new Date()
      const allowedNotificationState =
        (!notificationSettings?.result?.webPushNotif ||
          notificationPermissionStatus === 'default') &&
        notificationPermissionStatus !== 'denied'

      const shouldShowNotificationPrompt =
        itHasNotification && isPostponeDateExpired && allowedNotificationState

      if (shouldShowNotificationPrompt) {
        trackAmpEvent(AMP_EVENT_NAME.VIEW_PUSH_NOTIFICATION_PROMPT)

        dispatch(setShowNotificationRequest(true))
        showToastDialog({
          title: 'Get notified when new survey is available for you.',
          type: 'info',
          onClose: () => blockNotification('Close'),
          autoclose: false,
          disableIcon: true,
          primaryButtonTitle: `Yes, I'm In`,
          secondaryButtonTitle: 'Not Now',
          onClickPrimaryButton: grantNotification,
          onClickSecondaryButton: () => blockNotification('Later'),
        })

        dispatch(setPostponeNotificationDate(''))
      }
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [notificationSettings?.result?.webPushNotif],
  )

  useEffect(() => {
    if (!hasPartnerId) {
      handleNotificationRequest(showNotificationRequest)
    }
  }, [showNotificationRequest, handleNotificationRequest, hasPartnerId])

  return null
}
